function pad(value) {
  return String(value).padStart(2, "0");
}

function buildDate(year, month, day) {
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

function dateToIso(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function getLastBusinessDay(reference = new Date()) {
  const base = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  while (base.getDay() === 0 || base.getDay() === 6) {
    base.setDate(base.getDate() - 1);
  }
  return base;
}

export function parseDisplayDateToIso(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const match = raw.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/);
  if (!match) return "";
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = buildDate(year, month, day);
  return date ? dateToIso(date) : "";
}

export function formatIsoDateToDisplay(value) {
  const iso = parseDisplayDateToIso(value);
  if (!iso) return "";
  const [year, month, day] = iso.split("-");
  return `${day}/${month}/${year}`;
}

export function parseDisplayDateTimeToIso(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
    return raw.slice(0, 16);
  }

  const match = raw.match(
    /^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})\s+(\d{1,2}):(\d{2})$/
  );
  if (!match) return "";

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return "";

  const date = buildDate(year, month, day);
  if (!date) return "";
  return `${dateToIso(date)}T${pad(hour)}:${pad(minute)}`;
}

export function formatIsoDateTimeToDisplay(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
    const [datePart, timePart] = raw.split("T");
    const displayDate = formatIsoDateToDisplay(datePart);
    return displayDate ? `${displayDate} ${timePart.slice(0, 5)}` : "";
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  const isoDate = dateToIso(parsed);
  const hours = pad(parsed.getHours());
  const minutes = pad(parsed.getMinutes());
  return `${formatIsoDateToDisplay(isoDate)} ${hours}:${minutes}`;
}

export function getTodayRange(reference = new Date()) {
  const base = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  const iso = dateToIso(base);
  return { from: iso, to: iso };
}

export function getTodayIso(reference = new Date()) {
  return getTodayRange(reference).from;
}

export function getCurrentMonthRange(reference = new Date()) {
  const start = new Date(reference.getFullYear(), reference.getMonth(), 1);
  const end = new Date(reference.getFullYear(), reference.getMonth() + 1, 0);
  return { from: dateToIso(start), to: dateToIso(end) };
}

export function getPreviousMonthRange(reference = new Date()) {
  const start = new Date(reference.getFullYear(), reference.getMonth() - 1, 1);
  const end = new Date(reference.getFullYear(), reference.getMonth(), 0);
  return { from: dateToIso(start), to: dateToIso(end) };
}

export function getCurrentBusinessMonthRange(reference = new Date()) {
  const end = getLastBusinessDay(reference);
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  return { from: dateToIso(start), to: dateToIso(end) };
}

export function getLastThreeMonthsRange(reference = new Date()) {
  const start = new Date(reference.getFullYear(), reference.getMonth() - 2, 1);
  const end = new Date(reference.getFullYear(), reference.getMonth() + 1, 0);
  return { from: dateToIso(start), to: dateToIso(end) };
}

export function clampIsoDateToMax(value, maxValue) {
  const iso = parseDisplayDateToIso(value);
  const maxIso = parseDisplayDateToIso(maxValue);
  if (!iso) return "";
  if (!maxIso) return iso;
  return iso > maxIso ? maxIso : iso;
}

export function clampIsoDateToMin(value, minValue) {
  const iso = parseDisplayDateToIso(value);
  const minIso = parseDisplayDateToIso(minValue);
  if (!iso) return "";
  if (!minIso) return iso;
  return iso < minIso ? minIso : iso;
}

export function addDaysIso(value, deltaDays) {
  const iso = parseDisplayDateToIso(value);
  if (!iso) return "";
  const [year, month, day] = iso.split("-").map(Number);
  const date = buildDate(year, month, day);
  if (!date) return "";
  date.setDate(date.getDate() + Number(deltaDays || 0));
  return dateToIso(date);
}
